import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ticketsService from "../services/ticketsService";
import s3Service from "../services/s3Service";
import { useAuth } from "../contexts/AuthContext";
import "../assets/panels.css";
import Loader from "../components/Loader";

// Helper function to strip HTML tags and convert to plain text
const stripHtml = (html) => {
  if (!html) return "";
  // Create a temporary element to parse HTML
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const TicketNoteEdit = () => {
  const { id, noteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  console.log("TicketNoteEdit component loaded with params:", { id, noteId });

  const [note, setNote] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description: "",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [removingFileId, setRemovingFileId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    console.log("TicketNoteEdit useEffect triggered with:", { id, noteId });
    setLoading(true);
    setError(null);
    
    const loadData = async () => {
      try {
        console.log("Loading note data for noteId:", noteId);
        console.log("Loading ticket data for id:", id);
        
        // Test API connectivity first
        try {
          const testResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/ticketsnotes/${noteId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });
          console.log("Test API response status:", testResponse.status);
          console.log("Test API response ok:", testResponse.ok);
          
          if (!testResponse.ok) {
            const errorText = await testResponse.text();
            console.log("Test API error response:", errorText);
            throw new Error(`API Error: ${testResponse.status} - ${errorText}`);
          }
        } catch (testError) {
          console.error("API connectivity test failed:", testError);
          throw new Error(`API connectivity issue: ${testError.message}`);
        }
        
        const [noteData, ticketData] = await Promise.all([
          ticketsService.getTicketNote(noteId),
          ticketsService.getTicket(id),
        ]);

        console.log("Note data received:", noteData);
        console.log("Ticket data received:", ticketData);

        // Ticket #310: only the note's author may edit it (applies to all roles).
        if (noteData.user_id !== user?.id) {
          console.log(`🚫 User ${user?.id} attempted to edit note ${noteId} owned by user ${noteData.user_id}`);
          setError("You can only edit notes you created");
          setLoading(false);
          return;
        }

        setNote(noteData);
        setTicket(ticketData);
        setForm({
          description: stripHtml(noteData.description) || "",
        });
        
        // Load existing files from the note
        if (noteData.documents && Array.isArray(noteData.documents)) {
          setExistingFiles(noteData.documents);
          console.log("Loaded existing files:", noteData.documents);
        } else {
          setExistingFiles([]);
          console.log("No existing files found");
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error loading note data:", err);
        console.error("Error details:", {
          message: err.message,
          stack: err.stack,
          response: err.response
        });
        
        let errorMessage = "Failed to load note";
        if (err.message) {
          errorMessage = err.message;
        } else if (err.response && err.response.status) {
          errorMessage = `Server error: ${err.response.status}`;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };
    
    loadData();
  }, [noteId, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (s3Key, fileName) => {
    const uploadedFile = {
      name: fileName,
      s3Key: s3Key,
      uploadedAt: new Date()
    };
    
    setUploadedFiles(prev => [...prev, uploadedFile]);
    setError(null);
  };

  // Handle multiple file upload (images and documents)
  const handleMultipleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Validate file types and sizes
    const invalidFiles = [];
    const validFiles = [];
    // Laravel only allows images (jpg, jpeg, png) for ticket notes
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/jpg'
    ];

    files.forEach(file => {
      // Check file type - Laravel only allows JPG, JPEG, PNG for ticket notes
      const isValidType = allowedTypes.includes(file.type) ||
                         file.name.toLowerCase().endsWith('.jpg') ||
                         file.name.toLowerCase().endsWith('.jpeg') ||
                         file.name.toLowerCase().endsWith('.png');

      if (!isValidType) {
        invalidFiles.push(`${file.name} - only JPG, JPEG, PNG images allowed`);
        return;
      }

      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        invalidFiles.push(`${file.name} - too large (max 50MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles.length > 0) {
      setError(`Invalid files: ${invalidFiles.join(', ')}`);
      return;
    }

    if (validFiles.length === 0) {
      setError('No valid files selected');
      return;
    }

    setError(null);
    setUploading(true);

    // Upload each file
    let successCount = 0;
    let failCount = 0;
    let lastError = null;

    for (const file of validFiles) {
      try {
        console.log(`Uploading image: ${file.name}, type: ${file.type}`);

        // Upload as image (Laravel only allows images for ticket notes)
        const s3Key = await s3Service.uploadImage(file, {
          maxSize: 50 * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'],
          folder: 'documents'
        });

        console.log(`Successfully uploaded ${file.name} to S3 key: ${s3Key}`);

        // Add to uploaded files list
        const uploadedFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          s3Key: s3Key,
          uploadedAt: new Date()
        };

        setUploadedFiles(prev => [...prev, uploadedFile]);
        successCount++;

      } catch (uploadError) {
        console.error(`Failed to upload ${file.name}:`, uploadError);
        lastError = uploadError.message || 'Unknown error';
        failCount++;
      }
    }

    setUploading(false);

    // Show result message
    if (successCount > 0) {
      setError(null);
      if (failCount > 0) {
        setError(`${successCount} files uploaded, ${failCount} failed: ${lastError}`);
      }
    } else {
      setError(`Failed to upload: ${lastError}`);
    }

    // Clear the input
    event.target.value = '';
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openExistingFile = async (doc) => {
    try {
      console.log('🔍 Opening document via API:', doc.id);

      // Use the backend download endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/documents/${doc.id}/download`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob URL and open in new tab
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);

      setError(null);
      console.log('✅ Document opened successfully');
    } catch (err) {
      console.error("Error opening document:", err);
      setError("Failed to open document: " + err.message);
    }
  };

  const removeExistingFile = async (documentId) => {
    if (!window.confirm("Are you sure you want to remove this file and all its related records?")) {
      return;
    }

    setRemovingFileId(documentId);
    setError(null);

    try {
      // Use the enhanced deletion endpoint that removes related records
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/documents/${documentId}/delete-with-relations`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          documentable_id: noteId,
          documentable_type: 'ticketsnotes'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove file');
      }

      const result = await response.json();
      console.log("File and related records removed successfully:", result);

      // Remove the file from the existing files list
      setExistingFiles(prev => prev.filter(file => file.id !== documentId));
      
      // Show success message with details
      if (result.deletedRecords && result.deletedRecords.length > 0) {
        setError(null);
        console.log(`File removed along with ${result.deletedRecords.length} related records`);
      }
    } catch (err) {
      console.error("Error removing file:", err);
      setError(err.message || "Failed to remove file");
    } finally {
      setRemovingFileId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) {
      setError("Description is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare data as JSON (not FormData since we're using S3 for files)
      const noteData = {
        description: form.description.trim(),
        // Add uploaded files (S3 keys)
        attachments: uploadedFiles.map(file => ({
          name: file.name,
          s3Key: file.s3Key,
          size: file.size,
          type: file.type
        }))
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ticketsnotes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(noteData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update note');
      }

      const result = await response.json();
      console.log("Note updated successfully:", result);
      
      // Navigate back to ticket details screen
      navigate(`/tickets/${id}`);
    } catch (err) {
      console.error("Error updating note:", err);
      setError(err.message || "Failed to update note");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/tickets/${id}`);
  };

  const responsiveStyles = `
    .ticket-note-edit-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 32px 120px;
      background: white;
    }

    .ticket-note-edit-container .panel {
      border: none;
    }

    .ticket-note-edit-container .panel-title {
      border: none;
    }

    .ticket-note-edit-container .form-control {
      min-width: 100% !important;
    }

    @media (max-width: 1024px) {
      .ticket-note-edit-container {
        padding: 24px 40px;
      }
    }

    @media (max-width: 768px) {
      .ticket-note-edit-container {
        padding: 16px 20px;
      }
    }

    @media (max-width: 576px) {
      .ticket-note-edit-container {
        padding: 12px 10px;
      }

      .ticket-note-edit-container .breadcrumb-row {
        flex-wrap: wrap;
        gap: 4px;
      }

      .ticket-note-edit-container .form-group textarea {
        font-size: 16px !important;
        min-width: 100% !important;
      }

      .ticket-note-edit-container .panel-body {
        padding: 15px !important;
      }

      .ticket-note-edit-container .btn {
        width: 100%;
      }

      .ticket-note-edit-container .table th,
      .ticket-note-edit-container .table td {
        padding: 8px 6px !important;
        font-size: 12px !important;
      }

      .ticket-note-edit-container .table .btn {
        width: auto;
        padding: 2px 6px !important;
        font-size: 11px !important;
      }

      .ticket-note-edit-container .edit-actions-cell {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
    }
  `;

  if (loading) return <Loader />;

  // If there's an error but we have some data, show the form with the error
  if (error && !note && !ticket) {
    return (
      <>
        <style>{responsiveStyles}</style>
        <div className="container-fluid ticket-note-edit-container">
          <div className="breadcrumb-row">
            <span
              className="breadcrumb-main"
              style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </span>
            <span
              className="breadcrumb-main"
              style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
              onClick={() => navigate('/tickets')}
            >
              <span className="breadcrumb-sep">/</span>
              Tickets
            </span>
            <span
              className="breadcrumb-main"
              style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
              onClick={() => navigate(`/tickets/${id}`)}
            >
              <span className="breadcrumb-sep">/</span>
              Ticket {id}
            </span>
            <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-properties">Edit Note</span>
            </div>
          </div>

          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            Error: {error}
          </div>

          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>Unable to load note data. Please try refreshing the page or go back to the ticket.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
              style={{ marginRight: 8 }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!note) return <div style={{ padding: "20px" }}>Note not found</div>;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div className="container-fluid ticket-note-edit-container">
        {/* Breadcrumb */}
        <div className="breadcrumb-row">
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate('/tickets')}
          >
            <span className="breadcrumb-sep">/</span>
            Tickets
          </span>
          <span
            className="breadcrumb-main"
            style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
            onClick={() => navigate(`/tickets/${id}`)}
          >
            <span className="breadcrumb-sep">/</span>
            Ticket {id}
          </span>
          <div style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}>
            <span className="breadcrumb-sep">/</span>
            <span className="breadcrumb-properties">Edit Note {noteId}</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="panel mb-4">
          <div className="panel-title">
            <h3>Edit Ticket Note</h3>
          </div>
          <div className="panel-body">
              {/* Error Display */}
              {error && (
                <div className="alert alert-danger" style={{ marginBottom: '15px' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <fieldset className="form-horizontal">
                  {/* Description Field */}
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <div className="col-xs-12" style={{ width: '100%' }}>
                      <textarea
                        id="description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        className="form-control"
                        rows="8"
                        required
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          fontSize: '14px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                </fieldset>

                {/* Images Upload Field - Matching Laravel exactly */}
                <fieldset className="form-horizontal" style={{ marginTop: '30px' }}>
                  <div className="form-group">
                    <label style={{
                      fontWeight: 'bold',
                      marginBottom: '10px',
                      fontSize: '14px',
                      display: 'block',
                      paddingLeft: '15px'
                    }}>
                      Images (may choose multiple)
                    </label>
                    <div className="col-sm-5">
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                        onChange={handleMultipleFileUpload}
                        disabled={uploading}
                        className="form-control"
                        style={{
                          padding: '6px 12px',
                          fontSize: '14px',
                          border: '1px solid #ccc',
                          borderRadius: '4px'
                        }}
                      />
                      {uploading && (
                        <div style={{ marginTop: '8px', color: '#337ab7' }}>
                          Uploading...
                        </div>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* Newly Uploaded Files (pending save) */}
                {uploadedFiles.length > 0 && (
                  <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      New images to be added:
                    </label>
                    <ul style={{ listStyle: 'none', padding: 0, marginTop: '8px' }}>
                      {uploadedFiles.map((file, index) => (
                        <li key={index} style={{
                          padding: '5px 10px',
                          backgroundColor: '#dff0d8',
                          borderRadius: '4px',
                          marginBottom: '5px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>{file.name}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#d9534f',
                              cursor: 'pointer',
                              fontSize: '16px'
                            }}
                          >
                            &times;
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Save Note Button - Matching Laravel */}
                <div className="form-group text-right" style={{ marginTop: '20px' }}>
                  <button
                    type="submit"
                    disabled={saving || uploading}
                    className="btn btn-head btn-standard"
                    style={{
                      backgroundColor: '#1B2046',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 12px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      cursor: (saving || uploading) ? 'not-allowed' : 'pointer',
                      opacity: (saving || uploading) ? 0.6 : 1
                    }}
                  >
                    {saving ? "Saving..." : uploading ? "Uploading..." : "Save note"}
                  </button>
                </div>
              </form>

              {/* Images Section - Matching Laravel's table */}
              {existingFiles.length > 0 && (
                <div style={{ marginTop: '30px' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px' }}>Images:</h4>
                  <table className="table table-bordered" style={{ width: '100%', marginBottom: '20px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold' }}>Name</th>
                        <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold' }}>Uploaded</th>
                        <th style={{ padding: '8px', fontSize: '14px', fontWeight: 'bold' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingFiles.map((file) => (
                        <tr key={file.id}>
                          <td style={{ padding: '8px', fontSize: '14px' }}>{file.name}</td>
                          <td style={{ padding: '8px', fontSize: '14px' }}>
                            {file.created_at ? new Date(file.created_at).toLocaleDateString('en-GB') : 'Unknown'}
                          </td>
                          <td style={{ padding: '8px', fontSize: '14px' }}>
                            <div className="edit-actions-cell" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => openExistingFile(file)}
                                className="btn btn-sm btn-warning"
                                style={{
                                  padding: '2px 8px',
                                  fontSize: '12px',
                                  backgroundColor: '#f0ad4e',
                                  borderColor: '#eea236',
                                  color: '#fff'
                                }}
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                onClick={() => removeExistingFile(file.id)}
                                disabled={removingFileId === file.id}
                                className="btn btn-sm btn-danger"
                                style={{
                                  padding: '2px 8px',
                                  fontSize: '12px',
                                  backgroundColor: '#d9534f',
                                  borderColor: '#d43f3a',
                                  color: '#fff',
                                  opacity: removingFileId === file.id ? 0.6 : 1
                                }}
                              >
                                {removingFileId === file.id ? "Removing..." : "Remove"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketNoteEdit; 